import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { fantasyService } from "@/api/services";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Crown,
    Trophy,
    ArrowsClockwise,
    Lightning,
    ArrowRight,
    SignOut,
    Sparkle,
    Lightbulb,
} from "@phosphor-icons/react";

const POSITION_ORDER = { GKP: 0, DEF: 1, MID: 2, FWD: 3 };
const POSITION_LABEL = {
    GKP: "Goalkeepers",
    DEF: "Defenders",
    MID: "Midfielders",
    FWD: "Forwards",
};

const fdrColor = (fdr) => {
    if (fdr == null) return "bg-zinc-800 text-zinc-400";
    if (fdr <= 2) return "bg-primary/20 text-primary border-primary/40";
    if (fdr === 3) return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    return "bg-red-500/15 text-red-300 border-red-500/30";
};

const verdictColor = (v) => {
    if (v === "target") return "text-primary";
    if (v === "fade") return "text-red-300";
    return "text-amber-300";
};

const priorityColor = (p) => {
    if (p === "high") return "border-red-500/40 text-red-300";
    if (p === "low") return "border-zinc-700 text-zinc-400";
    return "border-amber-500/40 text-amber-300";
};

export default function Fantasy() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [teamIdInput, setTeamIdInput] = useState("");
    const [linking, setLinking] = useState(false);
    const [advice, setAdvice] = useState(null);
    const [adviceLoading, setAdviceLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const loadStatus = async () => {
        try {
            const s = await fantasyService.status();
            setStatus(s);
            return s;
        } catch (err) {
            toast.error(err?.message || "Could not load FPL status");
            return null;
        }
    };

    const loadAdvice = async (refresh = false) => {
        if (refresh) setRefreshing(true);
        else setAdviceLoading(true);

        const startedAt = Date.now();
        const MAX_WAIT_MS = 90_000; // 90s hard cap
        const POLL_DELAY_MS = 4_000;

        try {
            // First call may kick off a background job and return
            // `{status: "generating"}`. Poll until we get a "ready" payload
            // or the max-wait cap is hit.
            while (true) {
                const a = await fantasyService.advice(refresh);
                refresh = false; // Only pass ?refresh=true on the first call.
                if (a?.status === "ready" || a?.advice) {
                    setAdvice(a);
                    return;
                }
                if (a?.status === "failed") {
                    toast.error(a?.detail || a?.error || "AI advisor failed — please retry.");
                    return;
                }
                if (Date.now() - startedAt > MAX_WAIT_MS) {
                    toast.error(
                        "Still crunching — please tap Re-analyse again in a moment."
                    );
                    return;
                }
                await new Promise((r) => setTimeout(r, POLL_DELAY_MS));
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    err?.message ||
                    "Could not load advice"
            );
        } finally {
            setAdviceLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        (async () => {
            const s = await loadStatus();
            setStatusLoading(false);
            if (s?.isVip && s?.linkedTeamId) {
                loadAdvice();
            }
        })();
    }, []);

    const onLink = async (e) => {
        e.preventDefault();
        const id = teamIdInput.trim();
        if (!/^\d+$/.test(id)) {
            toast.error("Team ID must be a number");
            return;
        }
        setLinking(true);
        try {
            await fantasyService.link(id);
            toast.success("Team linked — generating advice…");
            const s = await loadStatus();
            if (s?.linkedTeamId) await loadAdvice();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || err?.message || "Could not link team"
            );
        } finally {
            setLinking(false);
        }
    };

    const onUnlink = async () => {
        if (!window.confirm("Unlink your FPL team? You can re-link any time.")) return;
        try {
            await fantasyService.unlink();
            setAdvice(null);
            await loadStatus();
            toast.success("Team unlinked");
        } catch (err) {
            toast.error(err?.message || "Could not unlink");
        }
    };

    return (
        <Layout>
            <Seo title="Fantasy Squad AI" path="/fantasy" noIndex />
            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10">
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-2">
                    ▌ Fantasy Premier League · AI assistant
                </div>
                <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter">
                    Your <span className="text-primary">Squad</span>, supercharged
                </h1>
                <p className="text-zinc-400 mt-3 max-w-2xl">
                    Link your FPL team and KickLoom&apos;s AI grades it against live form,
                    fixtures and ownership — captain pick, transfers, chip strategy
                    and differentials in seconds.
                </p>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                {statusLoading ? (
                    <SkeletonBlock />
                ) : !user ? (
                    <SignInGate />
                ) : !status?.isVip ? (
                    <VipGate />
                ) : !status?.linkedTeamId ? (
                    <LinkTeamForm
                        teamIdInput={teamIdInput}
                        setTeamIdInput={setTeamIdInput}
                        onLink={onLink}
                        linking={linking}
                        gameweek={status?.gameweek}
                    />
                ) : (
                    <LinkedView
                        status={status}
                        advice={advice}
                        adviceLoading={adviceLoading}
                        refreshing={refreshing}
                        onRefresh={() => loadAdvice(true)}
                        onUnlink={onUnlink}
                    />
                )}
            </div>
        </Layout>
    );
}

// ─── States ───────────────────────────────────────────────────────────────

function SkeletonBlock() {
    return (
        <div className="bg-card border border-border p-10 animate-pulse">
            <div className="h-4 w-32 bg-border mb-4" />
            <div className="h-10 w-2/3 bg-border" />
        </div>
    );
}

function SignInGate() {
    return (
        <div
            className="bg-card border border-border p-10 text-center"
            data-testid="fantasy-signin-gate"
        >
            <h2 className="text-2xl font-bold mb-3">Sign in to continue</h2>
            <p className="text-zinc-400 mb-6">
                The Fantasy AI assistant is a VIP feature — sign in or upgrade first.
            </p>
            <Link to="/login">
                <Button
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                    data-testid="fantasy-signin-btn"
                >
                    Sign in
                </Button>
            </Link>
        </div>
    );
}

function VipGate() {
    return (
        <div
            className="bg-card border border-border p-8 md:p-12 relative overflow-hidden"
            data-testid="fantasy-vip-gate"
        >
            <Crown
                size={120}
                weight="duotone"
                className="absolute -right-6 -top-6 text-primary/10"
            />
            <div className="relative max-w-2xl">
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-primary/10 text-primary border border-primary">
                    VIP exclusive
                </span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-4 mb-4">
                    Unlock the Fantasy AI assistant
                </h2>
                <ul className="space-y-3 text-zinc-300 mb-8">
                    <FeatureBullet>
                        Captain & vice picks with form + fixture reasoning
                    </FeatureBullet>
                    <FeatureBullet>
                        Personalised transfer suggestions for your squad
                    </FeatureBullet>
                    <FeatureBullet>
                        Chip strategy (Wildcard, Free Hit, Triple Captain, Bench Boost)
                    </FeatureBullet>
                    <FeatureBullet>
                        Differentials &lt; 15% ownership and fixture difficulty radar
                    </FeatureBullet>
                </ul>
                <Link to="/subscription">
                    <Button
                        className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                        data-testid="fantasy-upgrade-btn"
                    >
                        Upgrade to VIP <ArrowRight size={16} weight="bold" className="ml-2" />
                    </Button>
                </Link>
            </div>
        </div>
    );
}

function FeatureBullet({ children }) {
    return (
        <li className="flex items-start gap-3">
            <Sparkle size={16} weight="fill" className="text-primary mt-1 shrink-0" />
            <span>{children}</span>
        </li>
    );
}

function LinkTeamForm({ teamIdInput, setTeamIdInput, onLink, linking, gameweek }) {
    return (
        <div
            className="bg-card border border-border p-8 md:p-12 max-w-2xl"
            data-testid="fantasy-link-form"
        >
            {gameweek && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                    ▌ {gameweek.name} · deadline{" "}
                    {new Date(gameweek.deadline).toLocaleString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    })}
                </p>
            )}
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Link your FPL team
            </h2>
            <p className="text-zinc-400 mb-6">
                Find your Team ID in the FPL website URL after clicking{" "}
                <span className="text-primary">Points</span> in your team page —
                e.g. <span className="font-mono text-zinc-300">/entry/12345/</span>{" "}
                means your Team ID is{" "}
                <span className="font-mono text-primary">12345</span>.
            </p>
            <form onSubmit={onLink} className="space-y-4">
                <div>
                    <label
                        htmlFor="fpl-team-id"
                        className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2"
                    >
                        FPL Team ID
                    </label>
                    <Input
                        id="fpl-team-id"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={teamIdInput}
                        onChange={(e) => setTeamIdInput(e.target.value)}
                        placeholder="e.g. 12345"
                        className="rounded-none bg-background border-border focus:border-primary focus:ring-1 focus:ring-primary font-mono text-base"
                        data-testid="fantasy-team-id-input"
                        required
                    />
                </div>
                <Button
                    type="submit"
                    disabled={linking}
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider w-full md:w-auto"
                    data-testid="fantasy-link-submit"
                >
                    {linking ? "Linking…" : "Link team & analyse"}
                </Button>
            </form>
        </div>
    );
}

function LinkedView({ status, advice, adviceLoading, refreshing, onRefresh, onUnlink }) {
    return (
        <div className="space-y-8" data-testid="fantasy-linked-view">
            <ManagerHeader status={status} onUnlink={onUnlink} onRefresh={onRefresh} refreshing={refreshing} cachedAt={advice?.cached_at} />
            {adviceLoading || !advice ? (
                <AdviceSkeleton />
            ) : (
                <>
                    <SummaryCard summary={advice?.advice?.summary} />
                    <CaptainCard captain={advice?.advice?.captain} />
                    <TransfersCard transfers={advice?.advice?.transfers} />
                    <ChipStrategyCard chip={advice?.advice?.chip_strategy} chipStatus={advice?.chip_status} />
                    <DifferentialsCard differentials={advice?.advice?.differentials} />
                    <FixtureRadarCard radar={advice?.advice?.fixture_radar} />
                    <SquadCard squad={advice?.squad} />
                </>
            )}
        </div>
    );
}

function ManagerHeader({ status, onUnlink, onRefresh, refreshing, cachedAt }) {
    return (
        <div className="flex items-start justify-between gap-4 bg-card border border-border p-5 md:p-6">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                    ▌ Linked team
                </div>
                <div
                    className="text-2xl md:text-3xl font-bold"
                    data-testid="fantasy-team-name"
                >
                    {status.linkedTeamName}
                </div>
                {status.gameweek && (
                    <div className="font-mono text-xs text-zinc-500 mt-1">
                        {status.gameweek.name} · deadline{" "}
                        {new Date(status.gameweek.deadline).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </div>
                )}
                {cachedAt && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                        Analysis from {new Date(cachedAt).toLocaleString()}
                    </div>
                )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <Button
                    type="button"
                    onClick={onRefresh}
                    disabled={refreshing}
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                    data-testid="fantasy-refresh-btn"
                >
                    <ArrowsClockwise
                        size={14}
                        weight="bold"
                        className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
                    />
                    {refreshing ? "Analysing…" : "Re-analyse"}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={onUnlink}
                    className="rounded-none border-border hover:border-red-500 hover:text-red-300 font-mono uppercase tracking-wider text-xs"
                    data-testid="fantasy-unlink-btn"
                >
                    <SignOut size={14} weight="bold" className="mr-2" /> Unlink
                </Button>
            </div>
        </div>
    );
}

function AdviceSkeleton() {
    return (
        <div className="space-y-4">
            <div
                className="bg-card border border-primary/40 p-6 flex items-center gap-4"
                data-testid="fantasy-advice-loading"
            >
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-primary">
                        ▌ Analysing your squad
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                        Crunching form, fixtures and ownership — this usually takes
                        10–15 seconds on the first run.
                    </p>
                </div>
            </div>
            {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-card border border-border p-6 animate-pulse">
                    <div className="h-3 w-24 bg-border mb-3" />
                    <div className="h-8 w-2/3 bg-border" />
                </div>
            ))}
        </div>
    );
}

function SectionTitle({ icon: Icon, label, subtitle }) {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-primary">
                <Icon size={12} weight="fill" /> {label}
            </div>
            {subtitle && (
                <h3 className="text-xl md:text-2xl font-bold tracking-tight mt-1">
                    {subtitle}
                </h3>
            )}
        </div>
    );
}

function SummaryCard({ summary }) {
    if (!summary) return null;
    return (
        <div
            className="bg-card border border-primary/40 p-6 md:p-7 relative overflow-hidden"
            data-testid="fantasy-summary"
        >
            <Lightning
                size={80}
                weight="duotone"
                className="absolute -right-3 -top-3 text-primary/10"
            />
            <SectionTitle icon={Lightning} label="Headline" />
            <p className="text-lg md:text-xl text-zinc-100 leading-relaxed">
                {summary}
            </p>
        </div>
    );
}

function CaptainCard({ captain }) {
    if (!captain) return null;
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-captain">
            <SectionTitle icon={Crown} label="Captaincy" subtitle="Who to give the armband" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CaptainPick variant="primary" pick={captain.primary} testId="fantasy-captain-primary" />
                <CaptainPick variant="vice" pick={captain.vice} testId="fantasy-captain-vice" />
                <CaptainPick
                    variant="differential"
                    pick={captain.differential}
                    testId="fantasy-captain-differential"
                />
            </div>
        </div>
    );
}

function CaptainPick({ variant, pick, testId }) {
    if (!pick) return null;
    const labels = {
        primary: { tag: "Captain", color: "border-primary text-primary" },
        vice: { tag: "Vice", color: "border-zinc-500 text-zinc-300" },
        differential: { tag: "Differential", color: "border-amber-500/60 text-amber-300" },
    };
    const meta = labels[variant];
    return (
        <div className="border border-border p-4 hover:border-primary/40 transition-snappy" data-testid={testId}>
            <span
                className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${meta.color} mb-2`}
            >
                {meta.tag}
            </span>
            <div className="text-xl font-bold tracking-tight">{pick.name}</div>
            <div className="font-mono text-xs text-zinc-500 mt-1">
                {pick.team}
                {pick.ownership != null && ` · ${pick.ownership}% owned`}
            </div>
            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{pick.reason}</p>
        </div>
    );
}

function TransfersCard({ transfers }) {
    if (!transfers) return null;
    if (transfers.length === 0) {
        return (
            <div className="bg-card border border-border p-6" data-testid="fantasy-transfers">
                <SectionTitle icon={ArrowsClockwise} label="Transfers" subtitle="No moves recommended" />
                <p className="text-sm text-zinc-400">
                    Your squad looks well set this week — bank the transfer.
                </p>
            </div>
        );
    }
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-transfers">
            <SectionTitle icon={ArrowsClockwise} label="Transfers" subtitle="Suggested moves" />
            <div className="space-y-3">
                {transfers.map((t, i) => (
                    <div
                        key={i}
                        className="border border-border p-4 flex flex-col md:flex-row md:items-center gap-3"
                        data-testid={`fantasy-transfer-${i}`}
                    >
                        <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                            <div className="text-right">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-red-300 mb-0.5">
                                    Out
                                </div>
                                <div className="font-bold">{t.out}</div>
                            </div>
                            <ArrowRight size={20} weight="bold" className="text-primary" />
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-0.5">
                                    In
                                </div>
                                <div className="font-bold">{t.in}</div>
                            </div>
                        </div>
                        <div className="md:max-w-md">
                            <span
                                className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${priorityColor(t.priority)} mb-2`}
                            >
                                {t.priority || "medium"} priority
                            </span>
                            <p className="text-sm text-zinc-300 leading-relaxed">
                                {t.reason}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const CHIP_LABEL = {
    wildcard: "Wildcard",
    freehit: "Free Hit",
    bboost: "Bench Boost",
    "3xc": "Triple Captain",
    hold: "Hold all chips",
};

function ChipStrategyCard({ chip, chipStatus }) {
    if (!chip) return null;
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-chip">
            <SectionTitle icon={Trophy} label="Chip strategy" subtitle={CHIP_LABEL[chip.recommended_chip] || "Hold"} />
            <p className="text-sm text-zinc-300 leading-relaxed">{chip.reason}</p>
            {chip.target_gameweek > 0 && (
                <p className="font-mono text-xs text-primary mt-3">
                    ▶ Target: Gameweek {chip.target_gameweek}
                </p>
            )}
            {chipStatus && (
                <div className="flex flex-wrap gap-2 mt-4">
                    {Object.entries(CHIP_LABEL)
                        .filter(([k]) => k !== "hold")
                        .map(([k, label]) => {
                            const used = (chipStatus[k] || 0) > 0;
                            return (
                                <span
                                    key={k}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                                        used
                                            ? "border-zinc-700 text-zinc-500 line-through"
                                            : "border-primary/40 text-primary"
                                    }`}
                                >
                                    {label}
                                </span>
                            );
                        })}
                </div>
            )}
        </div>
    );
}

function DifferentialsCard({ differentials }) {
    if (!differentials?.length) return null;
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-differentials">
            <SectionTitle
                icon={Lightbulb}
                label="Differentials"
                subtitle="Low-owned, high-upside"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {differentials.map((d, i) => (
                    <div key={i} className="border border-border p-4" data-testid={`fantasy-diff-${i}`}>
                        <div className="flex items-baseline justify-between mb-1">
                            <div className="font-bold">{d.name}</div>
                            <span className="font-mono text-xs text-amber-300">
                                {d.ownership}%
                            </span>
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                            {d.team} · {d.position}
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">{d.reason}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FixtureRadarCard({ radar }) {
    if (!radar?.length) return null;
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-radar">
            <SectionTitle
                icon={Lightning}
                label="Fixture radar"
                subtitle="Next 5 GW difficulty"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {radar.map((r, i) => (
                    <div
                        key={i}
                        className="border border-border p-4 flex items-center gap-4"
                        data-testid={`fantasy-radar-${i}`}
                    >
                        <div className="flex-1">
                            <div className="flex items-baseline gap-2">
                                <div className="text-lg font-bold">{r.team}</div>
                                <span
                                    className={`font-mono text-[10px] uppercase tracking-widest ${verdictColor(r.verdict)}`}
                                >
                                    {r.verdict}
                                </span>
                            </div>
                            <p className="text-sm text-zinc-400 mt-1">{r.note}</p>
                        </div>
                        <div
                            className={`font-mono text-2xl font-black tabular-nums px-3 py-2 border ${fdrColor(r.next5_fdr_avg)}`}
                        >
                            {Number(r.next5_fdr_avg || 0).toFixed(1)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SquadCard({ squad }) {
    if (!squad?.length) return null;
    const grouped = squad.reduce((acc, p) => {
        const pos = p.position || "?";
        acc[pos] = acc[pos] || [];
        acc[pos].push(p);
        return acc;
    }, {});
    const positions = Object.keys(grouped).sort(
        (a, b) => (POSITION_ORDER[a] ?? 9) - (POSITION_ORDER[b] ?? 9)
    );
    return (
        <div className="bg-card border border-border p-6" data-testid="fantasy-squad">
            <SectionTitle icon={Crown} label="Current squad" subtitle="Your 15 picks" />
            <div className="space-y-5">
                {positions.map((pos) => (
                    <div key={pos}>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                            {POSITION_LABEL[pos] || pos}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                            {grouped[pos].map((p) => (
                                <div
                                    key={p.id}
                                    className={`border p-3 ${
                                        p.is_captain
                                            ? "border-primary"
                                            : p.starting
                                            ? "border-border"
                                            : "border-border/40 opacity-70"
                                    }`}
                                >
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-bold truncate">{p.name}</span>
                                        {p.is_captain && (
                                            <span className="font-mono text-[9px] text-primary font-bold ml-1">
                                                C
                                            </span>
                                        )}
                                        {p.is_vice_captain && !p.is_captain && (
                                            <span className="font-mono text-[9px] text-zinc-400 ml-1">
                                                V
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                                        {p.team} · £{p.price}
                                    </div>
                                    {p.news && (
                                        <div className="text-[10px] text-amber-300 mt-1 line-clamp-2">
                                            {p.news}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
